import React, { useRef } from 'react';
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from 'framer-motion';
import {
  Sparkles,
  LayoutTemplate,
  Gem,
  LineChart,
  Cpu,
  Check,
  Users,
  Target,
} from 'lucide-react';
import { Reveal } from '@/components/layout/Reveal';
import { CtaButton } from '@/components/site/CtaButton';
import { WhatsAppButton } from '@/components/site/WhatsAppButton';
import { CONSULTATION_PATH } from '@/lib/site';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';

const ICONS = { Sparkles, LayoutTemplate, Gem, LineChart, Cpu };

export const ServiceIcon = ({ icon, className = 'h-5 w-5' }) => {
  const Icon = ICONS[icon] || Sparkles;
  return (
    <Icon
      className={className}
      strokeWidth={1.4}
      style={{ color: 'var(--rmh-mocha)' }}
      aria-hidden="true"
    />
  );
};

/** Left half of a service: badge, number, title, overview, CTAs. */
export const ServiceIntro = ({ service, compact = false }) => {
  const { t } = useI18n();

  return (
    <>
      <div className="flex items-center gap-4">
        <span
          className={cn(
            'inline-flex items-center justify-center rounded-full border',
            compact ? 'h-11 w-11' : 'h-12 w-12',
          )}
          style={{
            backgroundColor: 'var(--rmh-surface-inset)',
            borderColor: 'var(--rmh-border)',
          }}
        >
          <ServiceIcon
            icon={service.icon}
            className={compact ? 'h-[1.15rem] w-[1.15rem]' : 'h-5 w-5'}
          />
        </span>
        <span className="rmh-num text-[0.78rem] tracking-[0.12em] text-[color:var(--rmh-muted-2)]">
          {service.number}
        </span>
      </div>

      <h2
        className={cn(
          'rmh-h2',
          compact
            ? 'mt-5 text-[1.5rem] xl:text-[1.95rem]'
            : 'mt-7 text-[1.7rem] sm:text-[2.05rem]',
        )}
      >
        {service.title}
      </h2>
      <p
        className={cn(
          'rmh-body',
          compact
            ? 'mt-4 text-[0.875rem] leading-[1.6] xl:text-[0.94rem]'
            : 'mt-5 text-[0.95rem]',
        )}
      >
        {service.overview}
      </p>

      <div className={cn('flex flex-wrap gap-3', compact ? 'mt-6' : 'mt-8')}>
        <CtaButton
          to={CONSULTATION_PATH}
          variant="primary"
          size="md"
          arrow
          testId={`services-${service.slug}-inquire-button`}
        >
          {t.common.inquireNow}
        </CtaButton>
        <WhatsAppButton
          variant="outline"
          size="md"
          testId={`services-${service.slug}-whatsapp-link`}
        />
      </div>
    </>
  );
};

const Panel = ({ icon: Icon, label, items, testId, compact }) => (
  <div
    data-testid={testId}
    className={cn('h-full rounded-[16px] border', compact ? 'p-5' : 'p-6')}
    style={{
      backgroundColor: 'var(--rmh-surface)',
      borderColor: 'var(--rmh-border)',
      boxShadow: 'var(--shadow-hairline)',
    }}
  >
    <div className="flex items-center gap-2.5">
      <Icon
        className="h-4 w-4 shrink-0"
        strokeWidth={1.5}
        style={{ color: 'var(--rmh-accent-2)' }}
        aria-hidden="true"
      />
      <p className="rmh-eyebrow text-[0.58rem]">{label}</p>
    </div>
    <ul
      className={cn(
        'flex flex-col',
        compact ? 'mt-3.5 gap-2.5' : 'mt-5 gap-3.5',
      )}
    >
      {items.map((line) => (
        <li key={line} className="flex gap-3">
          <span
            className="mt-[0.5rem] block h-px w-3.5 shrink-0"
            style={{ backgroundColor: 'var(--rmh-accent)' }}
            aria-hidden="true"
          />
          <span
            className={cn(
              'rmh-body',
              compact ? 'text-[0.82rem] leading-[1.5]' : 'text-[0.86rem]',
            )}
          >
            {line}
          </span>
        </li>
      ))}
    </ul>
  </div>
);

/** Right half of a service: who it's for, outcomes, what's included. */
export const ServicePanels = ({ service, compact = false }) => {
  const { t } = useI18n();

  return (
    <>
      <div
        className={cn(
          'grid grid-cols-1 sm:grid-cols-2',
          compact ? 'gap-4' : 'gap-5',
        )}
      >
        <Panel
          icon={Users}
          label={t.servicesPage.whoLabel}
          items={service.who}
          testId={`services-${service.slug}-who-panel`}
          compact={compact}
        />
        <Panel
          icon={Target}
          label={t.servicesPage.outcomesLabel}
          items={service.outcomes}
          testId={`services-${service.slug}-outcomes-panel`}
          compact={compact}
        />
      </div>

      <div
        className={cn(
          'rounded-[16px] border',
          compact ? 'mt-4 p-5' : 'mt-5 p-6',
        )}
        style={{
          backgroundColor: 'var(--rmh-canvas-2)',
          borderColor: 'var(--rmh-border)',
        }}
        data-testid={`services-${service.slug}-includes-panel`}
      >
        <p className="rmh-eyebrow text-[0.58rem]">{t.servicesPage.includesLabel}</p>
        <ul
          className={cn(
            'grid grid-cols-1 sm:grid-cols-2',
            compact ? 'mt-3.5 gap-2.5' : 'mt-5 gap-3.5',
          )}
        >
          {service.includes.map((line) => (
            <li key={line} className="flex gap-2.5">
              <Check
                className="mt-[3px] h-3.5 w-3.5 shrink-0"
                strokeWidth={2}
                style={{ color: 'var(--rmh-accent-2)' }}
                aria-hidden="true"
              />
              <span
                className={cn(
                  'rmh-body',
                  compact ? 'text-[0.82rem] leading-[1.5]' : 'text-[0.86rem]',
                )}
              >
                {line}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
};

/**
 * Stacked service block — used on phones, tablets, very short windows and
 * whenever "reduce motion" is on.
 *
 * It gets its own, quieter version of the cinema: the oversized numeral drifts
 * behind the title, and the two columns travel at slightly different rates so
 * the block parallaxes as it passes. All transform/opacity only, and the copy
 * is always at full size — nothing depends on the viewport being tall enough.
 */
export const ServiceBlock = ({ service, index }) => {
  const reduce = useReducedMotion();
  const blockRef = useRef(null);
  const flip = index % 2 === 1;

  const { scrollYProgress } = useScroll({
    target: blockRef,
    offset: ['start end', 'end start'],
  });

  const numeralY = useTransform(scrollYProgress, [0, 1], [48, -48]);
  const introY = useTransform(scrollYProgress, [0, 1], [16, -16]);
  const panelsY = useTransform(scrollYProgress, [0, 1], [-12, 12]);

  return (
    <div
      id={service.slug}
      ref={blockRef}
      data-testid={`services-block-${service.slug}`}
      className="rmh-scroll-margin border-t py-12 first:border-t-0 first:pt-0 lg:py-20"
      style={{ borderColor: 'var(--rmh-border)' }}
    >
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
        <div className={cn('lg:col-span-4', flip && 'lg:order-2')}>
          <motion.div
            style={reduce ? undefined : { y: introY }}
            className={cn(
              'relative lg:sticky lg:top-32',
              !reduce && 'rmh-willchange',
            )}
          >
            <motion.span
              aria-hidden="true"
              style={reduce ? undefined : { y: numeralY }}
              className="rmh-num rmh-numeral-ghost pointer-events-none absolute -top-10 right-0 -z-10 select-none text-[6.5rem] sm:text-[8.5rem] lg:-right-2 lg:-top-12 lg:text-[9.5rem]"
            >
              {service.number}
            </motion.span>
            <ServiceIntro service={service} />
          </motion.div>
        </div>
        <div className={cn('lg:col-span-8', flip && 'lg:order-1')}>
          <motion.div
            style={reduce ? undefined : { y: panelsY }}
            className={!reduce ? 'rmh-willchange' : undefined}
          >
            <Reveal delay={0.05}>
              <ServicePanels service={service} />
            </Reveal>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ServiceBlock;
